import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
} from "@material-ui/core";
import { normalizeColOptions } from "app/websocketManager/utils";
import GFFormFieldRenderer from "./GFFormFieldRenderer";
import { getColumnLabel } from "./GFTranslations";

const CONSENT_SIGNED_FOR_ID     = "1133";
const CONSENT_NOT_SIGNED_FOR_ID = "1282";
const CONSENT_SIGNED_BY_ID      = "1134";
const GUARDIAN_NAME_ID          = "1283";
const GUARDIAN_REL_ID           = "1284";
const ALWAYS_HIDDEN_IDS         = new Set(["1136", "1138"]);

const CONSENT_FOR_OPTIONS = [
  "Donor (Report - case studies - Testimonials)",
  "Annual Report",
  "Print/Online Media",
  "Social Media",
  "YouTube",
  "Website",
  "Newsletter",
  "Marketing Collaterals",
];

function deriveNotSignedFor(signedForValue) {
  if (!signedForValue) return CONSENT_FOR_OPTIONS.join(",");
  const selected = new Set(
    signedForValue
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
  );
  return CONSENT_FOR_OPTIONS.filter((opt) => !selected.has(opt)).join(",");
}

function buildSubRowPayload(subColumns, subFormData, tempRowId = "-1") {
  const payload = {};
  subColumns.forEach((col) => {
    const typeId = String(col.column_type_id ?? "1");
    const colOpts = normalizeColOptions(col.options);

    if (colOpts?.is_read_only || colOpts?.is_star_column) return;
    if (typeId === "7" || typeId === "19") return;
    if (col.is_default == 1 || col.default_type == 1) return;

    let val = subFormData[`col_${col.id}`] ?? "";

    if (val === "" || val === undefined || val === null) {
      if (typeId === "12") val = false;
      else if (typeId === "10" || typeId === "9") val = null;
    }

    if (typeId === "10") val = JSON.stringify(val ?? { reference_column_id: null, value: "" });
    if (typeId === "9") val = JSON.stringify(val ?? []);

    payload[col.id] = {
      value: val,
      column_type_id: typeId,
      row_id: tempRowId,
      is_modified: true,
    };

    if (typeId === "6") {
      payload[col.id].sub_list_id = colOpts?.sub_list_id;
    }
  });
  return payload;
}

function GFSubsheetForm({ column, subColumns, value, onChange, activeLanguage, error, store, data }) {
  const colOpts = normalizeColOptions(column?.options);
  const subListId = colOpts?.sub_list_id;

  const editableSubCols = (subColumns || [])
    .filter((col) => {
      const typeId = String(col.column_type_id ?? "1");
      const opts = normalizeColOptions(col.options);
      return (
        typeId !== "7" &&
        typeId !== "19" &&
        col.is_default != 1 &&
        col.default_type != 1 &&
        !opts?.is_read_only &&
        !opts?.is_star_column
      );
    })
    .map((col) => ({
      ...col,
      normalizedColumnOptions: normalizeColOptions(col.options),
    }));

  const [subFormData, setSubFormData] = useState({});
  const [hasSubRow, setHasSubRow] = useState(!!value);

  const consentSignedByVal = subFormData[`col_${CONSENT_SIGNED_BY_ID}`] ?? "";
  const isGuardian = String(consentSignedByVal).trim() === "Guardian";

  function isSubColVisible(colId) {
    const id = String(colId);
    if (ALWAYS_HIDDEN_IDS.has(id)) return false;
    if (id === GUARDIAN_NAME_ID || id === GUARDIAN_REL_ID) return isGuardian;
    return true;
  }

  function isSubColMandatory(col) {
    const id = String(col.id);
    if (id === GUARDIAN_NAME_ID || id === GUARDIAN_REL_ID) return isGuardian;
    const opts = normalizeColOptions(col.options);
    return opts?.is_mandatory === true || opts?.is_mandatory === "1" || opts?.is_mandatory === 1;
  }

  const handleSubFieldChange = (colId, newVal) => {
    let updated = { ...subFormData, [`col_${colId}`]: newVal };

    if (String(colId) === CONSENT_SIGNED_FOR_ID) {
      const notSigned = deriveNotSignedFor(newVal);
      updated[`col_${CONSENT_NOT_SIGNED_FOR_ID}`] = notSigned;
    }

    if (String(colId) === CONSENT_SIGNED_BY_ID && String(newVal).trim() !== "Guardian") {
      updated[`col_${GUARDIAN_NAME_ID}`] = "";
      updated[`col_${GUARDIAN_REL_ID}`] = "";
    }

    setSubFormData(updated);
    const subRowPayload = buildSubRowPayload(editableSubCols, updated);
    onChange({
      is_modified: true,
      column_type_id: "6",
      value: [subRowPayload],
      row_id: "",
      sub_list_id: subListId,
    });
  };

  const handleAddSubRow = () => {
    setHasSubRow(true);
    setSubFormData({});
    onChange(null);
  };

  const handleClearSubRow = () => {
    setHasSubRow(false);
    setSubFormData({});
    onChange(null);
  };

  const visibleSubCols = editableSubCols.filter((col) => isSubColVisible(col.id));

  return (
    <Box width="100%">
      <Typography variant="caption" style={{ fontWeight: 600, color: "#374151", marginBottom: 4, display: "block" }}>
        {getColumnLabel(column, activeLanguage)}
        <span style={{ fontWeight: 400, color: "#9ca3af", marginLeft: 6 }}>(Sub-form — one record)</span>
      </Typography>

      <Paper variant="outlined" style={{ borderRadius: 8, overflow: "hidden" }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          px={2}
          py={1}
          style={{ background: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}
        >
          <Typography variant="caption" style={{ fontWeight: 600, color: "#374151" }}>
            {visibleSubCols.length} field{visibleSubCols.length !== 1 ? "s" : ""}
          </Typography>
          {!hasSubRow ? (
            <Button
              size="small"
              variant="outlined"
              onClick={handleAddSubRow}
              style={{ borderColor: "#f59e0b", color: "#92400e", fontSize: 12, background: "#fff8e6" }}
            >
              + Add Consent Record
            </Button>
          ) : (
            <Button
              size="small"
              variant="outlined"
              onClick={handleClearSubRow}
              style={{ borderColor: "#fca5a5", color: "#dc2626", fontSize: 12 }}
            >
              ✕ Clear
            </Button>
          )}
        </Box>

        {!hasSubRow ? (
          <Box p={2} textAlign="center">
            <Typography variant="caption" style={{ color: "#9ca3af" }}>
              No consent record added. Click "+ Add Consent Record" to add one.
            </Typography>
          </Box>
        ) : (
          <Box
            p={2}
            display="grid"
            style={{ gridTemplateColumns: "1fr 1fr", gap: "12px 16px" }}
          >
            {visibleSubCols.map((subCol) => (
              <GFFormFieldRenderer
                key={subCol.id}
                column={{
                  ...subCol,
                  normalizedColumnOptions: {
                    ...subCol.normalizedColumnOptions,
                    is_mandatory: isSubColMandatory(subCol),
                    is_read_only: String(subCol.id) === CONSENT_NOT_SIGNED_FOR_ID
                      ? true
                      : subCol.normalizedColumnOptions?.is_read_only,
                  },
                }}
                value={subFormData[`col_${subCol.id}`] ?? ""}
                onChange={(val) => handleSubFieldChange(subCol.id, val)}
                activeLanguage={activeLanguage}
                error={null}
                store={store}
                data={data}
              />
            ))}
          </Box>
        )}
      </Paper>

      {error && (
        <Typography variant="caption" style={{ color: "#ef4444", marginTop: 4, display: "block" }}>
          ⚠ {error}
        </Typography>
      )}
    </Box>
  );
}

export default GFSubsheetForm;
